// Route definitions for all 4 UI areas defined in 03-frontend.md:
//   Public    /            → PublicLayout
//   Candidate /candidate/  → CandidateLayout  (role: candidate)
//   HRD       /hrd/        → HRDLayout         (role: hrd_admin)
//   Interviewer /interviewer/ → InterviewerLayout (role: interviewer)

import { createRouter, createWebHistory } from 'vue-router'
import { setupGuards } from './guards'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  },
  routes: [
    // Public area
    {
      path: '/',
      component: () => import('../layouts/PublicLayout.vue'),
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('../views/public/HomePage.vue'),
          meta: { public: true, title: 'Beranda' },
        },
        {
          path: 'jobs',
          name: 'public-jobs',
          component: () => import('../views/public/CareerPage.vue'),
          meta: { public: true, title: 'Karir' },
        },
        {
          path: 'jobs/by-id/:id',
          name: 'public-job-detail-by-id',
          component: () => import('../views/public/JobDetailPage.vue'),
          meta: { public: true, title: 'Detail Lowongan' },
        },
        {
          path: 'jobs/:companySlug/:jobSlug',
          name: 'public-job-detail',
          component: () => import('../views/public/JobDetailPage.vue'),
          meta: { public: true, title: 'Detail Lowongan' },
        },
        {
          path: 'about',
          name: 'about',
          component: () => import('../views/public/AboutPage.vue'),
          meta: { public: true, title: 'Tentang' },
        },
      ],
    },

    // Auth Pages (Standalone - No Navbar/Footer)
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/public/LoginPage.vue'),
      meta: { public: true, title: 'Masuk' },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/public/RegisterPage.vue'),
      meta: { public: true, title: 'Daftar' },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('../views/public/VerifyEmailPage.vue'),
      meta: { public: true, title: 'Verifikasi Email' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/public/ForgotPasswordPage.vue'),
      meta: { public: true, title: 'Lupa Sandi' },
    },
    {
      path: '/privacy',
      name: 'privacy-policy',
      component: () => import('../views/public/PrivacyPolicyPage.vue'),
      meta: { public: true, title: 'Kebijakan Privasi' },
    },
    {
      path: '/terms',
      name: 'terms-of-service',
      component: () => import('../views/public/TermsOfServicePage.vue'),
      meta: { public: true, title: 'Ketentuan Layanan' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/public/ResetPasswordPage.vue'),
      meta: { public: true, title: 'Reset Sandi' },
    },

    // Candidate area
    {
      path: '/candidate',
      component: () => import('../layouts/CandidateLayout.vue'),
      meta: { role: 'candidate' },
      children: [
        {
          path: 'dashboard',
          name: 'candidate-dashboard',
          component: () => import('../views/candidate/DashboardPage.vue'),
          meta: { title: 'Dashboard' },
        },
        {
          path: 'profile',
          name: 'candidate-profile',
          component: () => import('../views/candidate/ProfilePage.vue'),
          meta: { title: 'Profil Kandidat' },
        },
        {
          path: 'applications',
          name: 'candidate-applications',
          component: () => import('../views/candidate/ApplicationsPage.vue'),
          meta: { title: 'Lamaran Saya' },
        },
        {
          path: 'applications/:id',
          name: 'candidate-application-detail',
          component: () => import('../views/candidate/ApplicationDetailPage.vue'),
          props: true,
          meta: { title: 'Detail Lamaran' },
        },
        {
          path: 'notifications',
          name: 'candidate-notifications',
          component: () => import('../views/shared/NotificationsPage.vue'),
          meta: { title: 'Notifikasi' },
        },
      ],
    },

    // HRD Admin area
    {
      path: '/hrd',
      component: () => import('../layouts/HRDLayout.vue'),
      meta: { role: 'hrd_admin' },
      children: [
        {
          path: 'dashboard',
          name: 'hrd-dashboard',
          component: () => import('../views/hrd/DashboardPage.vue'),
          meta: { title: 'Dashboard HRD' },
        },
        {
          path: 'profile',
          name: 'hrd-profile',
          component: () => import('../views/shared/PersonalProfilePage.vue'),
          meta: { title: 'Profil Saya' },
        },
        {
          path: 'company-profile',
          name: 'hrd-company-profile',
          component: () => import('../views/hrd/CompanyProfilePage.vue'),
          meta: { title: 'Profil Perusahaan' },
        },
        {
          path: 'team',
          name: 'hrd-team',
          component: () => import('../views/hrd/TeamMembersPage.vue'),
          meta: { title: 'Tim Perusahaan' },
        },
        {
          path: 'jobs',
          name: 'hrd-jobs',
          component: () => import('../views/hrd/JobListPage.vue'),
          meta: { title: 'Manajemen Lowongan' },
        },
        {
          path: 'jobs/new',
          name: 'hrd-jobs-new',
          component: () => import('../views/hrd/JobFormPage.vue'),
          meta: { title: 'Buat Lowongan' },
        },
        {
          path: 'jobs/:id/edit',
          name: 'hrd-jobs-edit',
          component: () => import('../views/hrd/JobFormPage.vue'),
          props: true,
          meta: { title: 'Edit Lowongan' },
        },
        {
          path: 'applications',
          name: 'hrd-applications',
          component: () => import('../views/hrd/ApplicationsPage.vue'),
          meta: { title: 'Manajemen Lamaran' },
        },
        {
          path: 'applications/:id',
          name: 'hrd-application-detail',
          component: () => import('../views/hrd/ApplicationDetailPage.vue'),
          props: true,
          meta: { title: 'Detail Lamaran' },
        },
        {
          path: 'interviews',
          name: 'hrd-interviews',
          component: () => import('../views/hrd/InterviewsPage.vue'),
          meta: { title: 'Jadwal Wawancara' },
        },
        {
          path: 'interviews/:id',
          name: 'hrd-interview-detail',
          component: () => import('../views/hrd/InterviewDetailPage.vue'),
          props: true,
          meta: { title: 'Detail Wawancara' },
        },
        {
          path: 'reports',
          name: 'hrd-reports',
          component: () => import('../views/hrd/ReportsPage.vue'),
          meta: { title: 'Laporan Analitik' },
        },
        {
          path: 'audit',
          name: 'hrd-audit',
          component: () => import('../views/hrd/AuditPage.vue'),
          meta: { title: 'Jejak Audit' },
        },
        {
          path: 'notifications',
          name: 'hrd-notifications',
          component: () => import('../views/shared/NotificationsPage.vue'),
          meta: { title: 'Notifikasi' },
        },
      ],
    },

    // Interviewer area
    {
      path: '/interviewer',
      component: () => import('../layouts/InterviewerLayout.vue'),
      meta: { role: 'interviewer' },
      children: [
        {
          path: '',
          redirect: { name: 'interviewer-dashboard' },
        },
        {
          path: 'dashboard',
          name: 'interviewer-dashboard',
          component: () => import('../views/interviewer/DashboardPage.vue'),
          meta: { title: 'Dashboard Interviewer' },
        },
        {
          path: 'profile',
          name: 'interviewer-profile',
          component: () => import('../views/shared/PersonalProfilePage.vue'),
          meta: { title: 'Profil Saya' },
        },
        {
          path: 'company-profile',
          name: 'interviewer-company-profile',
          component: () => import('../views/shared/CompanyProfilePage.vue'),
          meta: { title: 'Profil Perusahaan' },
        },
        {
          path: 'team',
          name: 'interviewer-team',
          component: () => import('../views/interviewer/TeamPage.vue'),
          meta: { title: 'Tim Interviewer' },
        },
        {
          path: 'evaluation/:id',
          name: 'interviewer-evaluation',
          component: () => import('../views/interviewer/EvaluationPage.vue'),
          props: (route) => ({ id: route.params.id, isZen: route.query.isZen === 'true', compact: route.query.compact === 'true' }),
          meta: { title: 'Evaluasi Kandidat' },
        },
        {
          path: 'interviews/:id',
          redirect: (to) => ({ name: 'interviewer-evaluation', params: to.params }),
        },
        {
          path: 'notifications',
          name: 'interviewer-notifications',
          component: () => import('../views/shared/NotificationsPage.vue'),
          meta: { title: 'Notifikasi' },
        },
      ],
    },

    // 403 Unauthorized
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('../views/public/UnauthorizedPage.vue'),
      meta: { public: true, title: 'Akses Ditolak' },
    },

    // 404 catch-all
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundPage.vue'),
      meta: { public: true, title: 'Halaman Tidak Ditemukan' },
    },
  ],
})

setupGuards(router)

// [W1-02] Catch chunk-load failures and other router errors.
// Without this, a failed dynamic import() leaves RouterView empty (blank page)
// because the previous component already left due to Transition mode="out-in".
router.onError((err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err)
  console.error('[Router] Navigation error:', msg)

  // Chunk load failure (network error, missing chunk, version mismatch)
  if (msg.includes('chunk') || msg.includes('Loading chunk') || msg.includes('Failed to fetch')) {
    // Attempt a hard reload to re-fetch fresh chunks
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/503')) {
      window.location.reload()
    }
    return
  }

  // Other router errors — redirect to a safe page
  // (non-critical errors are logged but don't block navigation)
})

export default router
